import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { GlobeHemisphereWestIcon as Globe } from "@phosphor-icons/react";

/** True when a /connect/account error means the international seller
 *  agreement must be accepted first. */
export function isIntlAgreementError(err) {
  const d = err?.response?.data?.detail;
  return !!(d && typeof d === "object" && d.code === "intl_agreement_required");
}

/**
 * Click-wrap for sellers outside the US. The backend refuses to create
 * their Stripe account until this exact agreement version is recorded
 * (user, version, timestamp, IP). On accept we call `onAccepted` so the
 * caller can retry onboarding.
 */
export const IntlSellerAgreementDialog = ({ open, onOpenChange, onAccepted }) => {
  const [agreement, setAgreement] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChecked(false);
    api.get("/connect/intl-agreement")
      .then((r) => setAgreement(r.data))
      .catch(() => setAgreement(null));
  }, [open]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/connect/intl-agreement/accept");
      onOpenChange(false);
      onAccepted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="intl-seller-agreement-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl flex items-center gap-2">
            <Globe size={22} weight="duotone" /> International Seller Payment Agreement
          </DialogTitle>
          <DialogDescription>
            Because you're located outside the United States, sales are
            processed on <strong>your own Stripe account</strong> — please
            read and accept before payment setup.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-[#E8D9A6] bg-[#FBF7EC] p-4 max-h-64 overflow-y-auto text-[12.5px] leading-relaxed text-[#3B0E07] whitespace-pre-wrap" data-testid="intl-agreement-text">
          {agreement?.text || "Loading agreement…"}
        </div>
        <label className="flex items-start gap-2 cursor-pointer text-[13px] text-ink">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-[#7A1F12]"
            data-testid="intl-agreement-checkbox"
          />
          <span>
            I have read and accept the International Seller Payment Agreement
            ({agreement?.version || "…"}). I understand I am the merchant of
            record and Armstrong K9 Registry is never responsible for my payments.
          </span>
        </label>
        <DialogFooter>
          <Button
            onClick={accept}
            disabled={!checked || busy || !agreement}
            className="rounded-full bg-ink hover:bg-crimson text-white w-full h-11"
            data-testid="intl-agreement-accept-btn"
          >
            {busy ? "Recording…" : "Accept & continue to Stripe"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IntlSellerAgreementDialog;
